import logoUrl from "$assets/logos/elephant-logo-colour-501px_104px.png";
import { Button } from "$components/button/button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogHeader } from "$components/dialog/dialog-header";
import type { Territory } from "$global-providers/config-provider/provider";
import { useConfig } from "$global-providers/config-provider/provider";
import { useUser } from "$global-providers/user-provider";
import { TERRITORY } from "$routes/_lib/constants";
import {
  useExistingUserWhatsappConsentOverlayUiDefaults,
  whatsappConsentUiDefaultVariables,
} from "$routes/_lib/overlays/existing-user-whatsapp-consent-overlay/use-existing-user-whatsapp-consent-overlay-ui-defaults";
import { QUERY as USE_UI_DEFAULT_QUERY } from "$routes/user/_lib/use-ui-default";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, styled } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import * as Sentry from "@sentry/react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useFirstSignInUpdateUserConsentMutation } from "./__generated__/consent-modal.gql";
import { FormValues, formValidationSchema } from "./common";
import { MarketingConsent } from "./marketing-consent";
import { DefaultPrivacyPolicy } from "./privacy-policy/default";
import { KenyaPrivacyPolicy } from "./privacy-policy/kenya";
import { PakistanPrivacyPolicy } from "./privacy-policy/pakistan";

// TODO: It's not ideal that we replicate the UI default values here, but we're rushing.
// Nicer would be to push around the "is this the user's first sign in" state so you could
// leverage that in the ExistingUsersWhatsappConsentOverlay.
gql`
mutation FirstSignInUpdateUserConsent($id: String!, $privacyPolicy: Boolean!, $marketing: Boolean!, $whatsappConsent: Boolean, $softwareTermsOfUse: Boolean!, $uiDefaultArgs: UpsertUiDefaultInput!) {
  updateUser(input: {_id: $id, consent: { privacyPolicy: $privacyPolicy, marketing: $marketing, softwareTermsOfUse: $softwareTermsOfUse }, person: { whatsappConsent: $whatsappConsent } } ) {
    _id
    person {
      whatsappConsent
    }
  }
  upsertUiDefault(input: $uiDefaultArgs) {
    id
    namespace
    name
    scope
    value
  }
}
`;

export const ConsentModal: FC<{
  open?: boolean;
  onCompleted?: () => void;
}> = ({ open, onCompleted }) => {
  const { territory } = useConfig();
  const { id: userId } = useUser();

  const { save } = useExistingUserWhatsappConsentOverlayUiDefaults();

  const [updateConsent, { loading }] = useFirstSignInUpdateUserConsentMutation({
    // Similarly, this is a big rush, and duplicated from the ExistingUserWhatsappConsentOverlay.
    // Possible more correct fix would be to make UI default's id field an entity ID so we can benefit
    // from auto-cache updates purely by fetching its id.
    update: (cache, { data }) => {
      cache.writeQuery({
        query: USE_UI_DEFAULT_QUERY,
        variables: { args: whatsappConsentUiDefaultVariables },
        data: { UiDefault: data.upsertUiDefault },
      });
    },
  });
  const handleSubmit = async (values: FormValues) => {
    await updateConsent({
      variables: {
        id: userId,
        ...values,
        // TODO: Rushing. Not ideal this component has to know about the shape of the ui default.
        uiDefaultArgs: {
          ...whatsappConsentUiDefaultVariables,
          value: { hasSeen: true },
        },
      },
    }).catch((error) => Sentry.captureException(error)); // we don't want to stop progress here if there is an error; let's capture it in Sentry and continue
    save(true);
    onCompleted?.();
  };

  return (
    <ConsentModalContainer
      open={open}
      territory={territory}
      onAccept={handleSubmit}
      acceptLoading={loading}
    />
  );
};

const Logo = styled("img")({
  maxHeight: 52,
  alignSelf: "center",
});

const Form = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  width: "100%",
}));

const privacyPolicyOptions = {
  kenya: KenyaPrivacyPolicy,
  pakistan: PakistanPrivacyPolicy,
  default: DefaultPrivacyPolicy,
};

const ConsentModalContainer: FC<{
  open?: boolean;
  territory: Territory;
  onAccept: (values: FormValues) => void;
  acceptLoading?: boolean;
}> = ({ open, territory, onAccept, acceptLoading }) => {
  const formMethods = useForm<FormValues>({
    mode: "onTouched",
    resolver: yupResolver(formValidationSchema),
    defaultValues: {
      // Only Kenya is explicity opt-in.
      privacyPolicy: territory !== TERRITORY.KENYA,
      softwareTermsOfUse: true,
    },
  });

  const PrivacyPolicy =
    privacyPolicyOptions[territory] || privacyPolicyOptions.default;

  return (
    <DialogContainer open={open}>
      <DialogBody>
        <DialogHeader title="" style={{ justifyContent: "center" }}>
          <Logo src={logoUrl} alt="Elephant Logo" />
        </DialogHeader>
        <Typography variant="h3" component="h1" gutterBottom>
          <FormattedMessage id="auth.consent.title" defaultMessage="Consent" />
        </Typography>
        <FormProvider {...formMethods}>
          <Form
            name="consent-form"
            onSubmit={formMethods.handleSubmit(onAccept)}
          >
            <PrivacyPolicy />
            <Divider />
            <MarketingConsent />
            <Button
              type="submit"
              variant="contained"
              style={{ alignSelf: "flex-end" }}
              loading={acceptLoading}
            >
              <FormattedMessage
                id="first-sign-in.consent.submitButton"
                defaultMessage="Accept & Continue"
              />
            </Button>
          </Form>
        </FormProvider>
      </DialogBody>
    </DialogContainer>
  );
};
